/**
 * DocumentListItem — Single document row in the left panel.
 *
 * Fixed-slot pattern:
 *   Browse mode  → 40×40 DocumentThumbnail
 *   Selection mode → 40×40 centered inline checkbox
 *
 * Visual states:
 *   Active   → bg-brand-solid with white text + shadow
 *   Selected → bg-brand-primary with ring
 *   Exported → muted (text-disabled)
 *   Default  → hover:bg-primary_hover
 */
import { Check } from "@untitledui/icons";
import { BadgeWithDot } from "@/components/base/badges/badges";
import type { HomeDocument } from "@/types/home";
import { cx } from "@/utils/cx";
import { DocumentThumbnail } from "./document-thumbnail";
import { STATUS_MAP, formatDate } from "./helpers";

interface DocumentListItemProps {
    doc: HomeDocument;
    isActive: boolean;
    isSelectionMode: boolean;
    isSelected: boolean;
    /** Whether the checkbox is disabled (document not selectable for export) */
    isDisabled?: boolean;
    onSelect: () => void;
    onActivate: () => void;
}

export function DocumentListItem({ doc, isActive, isSelectionMode, isSelected, isDisabled = false, onSelect, onActivate }: DocumentListItemProps) {
    const isExported = doc.status === "exported";
    const statusConfig = STATUS_MAP[doc.status];
    const StatusIcon = statusConfig.icon;
    /** For in-progress documents, show the status label (e.g. "Finding Vendor") instead of the document type. */
    const subText = statusConfig.animate || doc.status === "matching" ? statusConfig.label : doc.documentType;

    const handleClick = () => {
        if (isSelectionMode) {
            if (!isDisabled) onSelect();
        } else {
            onActivate();
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className={cx(
                "group relative flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition duration-100 ease-linear",
                isDisabled && isSelectionMode ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                isActive && !isSelectionMode && "bg-brand-solid text-fg-white shadow-md",
                isSelected && isSelectionMode && !isDisabled && "bg-brand-primary shadow-xs ring-1 ring-brand",
                !isActive && !isSelected && !isDisabled && "hover:bg-primary_hover",
            )}
        >
            {/* Fixed-slot: Thumbnail or Checkbox */}
            <div className="flex size-10 shrink-0 items-center justify-center">
                {isSelectionMode ? (
                    <div
                        className={cx(
                            "flex size-5 items-center justify-center rounded-md border transition duration-100 ease-linear",
                            isDisabled
                                ? "cursor-not-allowed border-disabled bg-disabled opacity-50"
                                : isSelected
                                  ? "border-brand-solid bg-brand-solid text-fg-white"
                                  : "border-primary bg-primary group-hover:border-brand",
                        )}
                    >
                        {isSelected && !isDisabled && <Check className="size-3.5" />}
                    </div>
                ) : (
                    <DocumentThumbnail status={doc.status} />
                )}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
                <div className="mb-0.5">
                    <span
                        className={cx(
                            "block truncate text-sm font-semibold",
                            isActive && !isSelectionMode ? "text-fg-white" : isExported ? "text-disabled" : "text-primary",
                        )}
                    >
                        {doc.vendorName}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className={cx("truncate text-xs", isActive && !isSelectionMode ? "text-[var(--color-brand-200)]" : "text-quaternary")}>
                        {subText} &middot; {formatDate(doc.documentDate)}
                    </span>
                    <div className="ml-2 flex items-center gap-1.5">
                        {!isSelectionMode && (
                            <>
                                {doc.isRetentionExpired && (
                                    <span title="This document is expired and will be deleted at the end of the month" className="inline-flex shrink-0">
                                        <BadgeWithDot
                                            color="error"
                                            size="sm"
                                            className={cx("!px-1.5 !py-0 !text-[10px]", isActive && "!border-white/30 !bg-white/15 !text-white/80")}
                                        >
                                            Expired
                                        </BadgeWithDot>
                                    </span>
                                )}
                                {statusConfig.animate ? (
                                    <span
                                        className={cx(
                                            "inline-flex size-3.5 shrink-0 animate-spin items-center justify-center [&_svg]:size-3.5 [&_svg]:shrink-0",
                                            isActive ? "[&_svg]:text-white/70" : "[&_svg]:text-fg-brand-primary",
                                        )}
                                    >
                                        <StatusIcon />
                                    </span>
                                ) : (
                                    <StatusIcon
                                        className={cx(
                                            "size-3.5",
                                            statusConfig.animate && "animate-pulse",
                                            isActive
                                                ? "text-white/70"
                                                : cx(
                                                      statusConfig.badgeColor === "success" && "text-fg-success-primary",
                                                      statusConfig.badgeColor === "warning" && "text-fg-warning-primary",
                                                      statusConfig.badgeColor === "error" && "text-fg-error-primary",
                                                      statusConfig.badgeColor === "brand" && "text-fg-brand-primary",
                                                      statusConfig.badgeColor === "blue" && "text-[#2e90fa]",
                                                      statusConfig.badgeColor === "purple" && "text-[#7a5af8]",
                                                      statusConfig.badgeColor === "pink" && "text-[#f63d68]",
                                                      statusConfig.badgeColor === "gray" && "text-fg-quaternary",
                                                  ),
                                        )}
                                    />
                                )}
                                {isExported && (
                                    <BadgeWithDot
                                        color="gray"
                                        size="sm"
                                        className={cx("!px-1.5 !py-0 !text-[10px]", isActive && "!border-white/30 !bg-white/15 !text-white/80")}
                                    >
                                        Sent
                                    </BadgeWithDot>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </button>
    );
}
